import { useTranslation } from 'react-i18next';

import { ActionValueLabel } from '@/modules/rules/components/ActionsField/ActionValueLabel';
import { ACTION_ICONS } from '@/modules/rules/components/ActionsField/constants';
import {
  getActionLabel,
  hasActionValue,
  needsActionValue,
} from '@/modules/rules/components/ActionsField/utils';
import { Action } from '@/shared/types/rules';

const ActionRow = ({ action }: { action: Action }) => {
  const { t } = useTranslation();
  const Icon = ACTION_ICONS[action.type];
  const label = getActionLabel(t, action.type);
  const showValue = needsActionValue(action.type) && hasActionValue(action);

  return (
    <li className="flex min-w-0 items-center gap-1">
      {Icon && <Icon aria-hidden="true" className="size-4 shrink-0 opacity-70" />}
      <span className="truncate leading-5">
        {showValue ? <ActionValueLabel action={action} fallback={label} /> : label}
      </span>
    </li>
  );
};

type RuleTooltipContentProps = { name: string; description: string; actions: Action[] };

export const RuleTooltipContent = ({ name, description, actions }: RuleTooltipContentProps) => (
  <div className="text-xs">
    <div className="flex flex-col gap-0.5 px-3 py-2.5">
      <p className="leading-4 font-semibold">{name}</p>
      {description && <p className="leading-5">{description}</p>}
    </div>
    {actions.length > 0 && (
      <ul className="flex flex-col gap-3 border-t-[0.5px] border-t-muted/40 px-3 pt-2.5 pb-3 opacity-80">
        {actions.map((action, index) => (
          // Actions have no id, and a rule can repeat a type.
          // eslint-disable-next-line react/no-array-index-key
          <ActionRow key={`${action.type}-${index}`} action={action} />
        ))}
      </ul>
    )}
  </div>
);
